import type { ChangeEvent, FC } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { getEmptyAddress } from "@jti/commerce";
import { RadioButton, RadioButtonGroup } from "@jti/core-components";
import { isAddressFormExtendedWithProvince } from "@jti/fe-common";
import { Controller, useFormContext } from "@jti/ui";

import type { BillingAddressFieldConfig } from "@checkout-v2/formFields/factories/billingAddressFieldFactory";
import type { IsBillingAddressSameAsShippingFieldConfig } from "@checkout-v2/formFields/factories/isBillingAddressSameAsShippingFieldFactory";

type IsBillingSameAsShippingFieldProps = {
  fieldConfig: IsBillingAddressSameAsShippingFieldConfig;
  onChangeCallback?: (value: boolean) => void;
  billingConfig: BillingAddressFieldConfig;
};

export const IsBillingSameAsShippingField: FC<IsBillingSameAsShippingFieldProps> = ({
  fieldConfig,
  onChangeCallback,
  billingConfig,
}) => {
  const { control, setValue, getValues } = useFormContext();
  const { addressFormVariant } = useConfig();

  const isAddressExtendedWithProvince = isAddressFormExtendedWithProvince(addressFormVariant);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value === "true";

    setValue(fieldConfig.name, newValue);

    const currentBillingAddress = getValues(billingConfig.name);

    if (newValue === false) {
      setValue(
        billingConfig.name,
        getEmptyAddress({
          currentAddress: currentBillingAddress,
          overrides: {
            countryCode: billingConfig.emptyAddress.countryCode,
            countryName: billingConfig.emptyAddress.countryName ?? "",
            region: isAddressExtendedWithProvince ? currentBillingAddress?.region : undefined,
            regionCode: isAddressExtendedWithProvince ? currentBillingAddress?.regionCode : undefined,
          },
        })
      );
    }

    onChangeCallback?.(newValue);
  };

  return (
    <Controller
      name={fieldConfig.name}
      control={control}
      render={({ field }) => (
        <RadioButtonGroup name={field.name}>
          {fieldConfig.options.map((option) => (
            <RadioButton
              key={option.value.toString()}
              value={option.value.toString()}
              label={option.label}
              checked={field.value === option.value}
              onChange={onChange}
            />
          ))}
        </RadioButtonGroup>
      )}
    />
  );
};
